import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connectInfiniteHits, connectStateResults } from 'react-instantsearch-dom'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import CoachCardOptimized from '@components/coach-card-optimized'
import SrOnly from '@components/atoms/sr-only'
import AddIcon from '@material-ui/icons/Add'
import useCoachesBookThisWeek from '@hooks/useCoachesBookThisWeek'
import { useStyles } from './CoachResults.styles'
import { NoResultsPage } from './NoResultsPage'
import { shouldShowDirectoryResultsLoader } from './directory-results-loading'
import { DirectoryHydrateContext } from './directory-hydrate-context'
import DirectoryCoachCardSkeleton, {
  DIRECTORY_SKELETON_CARD_COUNT,
} from './directory-results-skeleton'

const LoadingIndicator = connectStateResults(
  ({ searching }) => (searching ? 'Loading...' : 'Load More'),
)

function LoadMoreButton({ hits, hasMore, refineNext }) {
  const classes = useStyles()
  if (hits.length && hasMore) {
    return (
      <div className={classes.moreResultsControlArea}>
        <Button
          className={classes.moreSearchResultsBtn}
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={!hasMore}
          onClick={refineNext}
        >
          <LoadingIndicator />
        </Button>
      </div>
    )
  }
  return <div />
}

LoadMoreButton.propTypes = {
  hits: PropTypes.array,
  hasMore: PropTypes.bool,
  refineNext: PropTypes.func,
}

const keyForHit = (hit) => {
  return hit.customSlug || hit.objectID
}

export function DirectoryHitsView({
  hits: hitsProp = [],
  hasMore,
  refineNext,
  hasPrevious,
  refinePrevious,
  resultsString,
  placeSearchValue,
  savedOnly = false,
  isCoachSaved,
  onToggleSavedCoach,
  onToggleSavedOnly,
  onClearDirectoryFilters,
  searching = false,
  isSearchStalled = false,
  searchResults,
  error,
  isLocationHydrating: hydratingProp = false,
  bookThisWeek = false,
  bookThisWeekJoinReady = true,
}) {
  const classes = useStyles()
  const hits = Array.isArray(hitsProp) ? hitsProp : []
  const hydratingFromContext = React.useContext(DirectoryHydrateContext)
  const isLocationHydrating = hydratingProp || hydratingFromContext
  const isBookableThisWeek = useCoachesBookThisWeek()

  useEffect(() => {
    if (hasPrevious) {
      refinePrevious()
    }
  }, [hits])

  // Saved-only is a CLIENT-SIDE filter on the hits Algolia has already
  // returned for the current query/geo. The infinite-hits connector keeps
  // owning pagination, so toggling this on doesn't issue a new search — it
  // just hides coaches that the user hasn't saved. Note: with infinite
  // pagination, a saved coach must still be inside the loaded window to
  // show up; this matches how `/golf-lessons` handles saved offerings.
  const visibleHits = savedOnly && typeof isCoachSaved === 'function'
    ? hits.filter((hit) => isCoachSaved(hit.objectID))
    : hits
  const savedOnlyEmpty = savedOnly && hits.length > 0 && visibleHits.length === 0
  const thisWeekJoinPending = bookThisWeek && !bookThisWeekJoinReady
  const showLoader = shouldShowDirectoryResultsLoader({
    hitCount: visibleHits.length,
    savedOnlyEmpty,
    isLocationHydrating,
    searching,
    isSearchStalled,
    searchResults,
    error,
    thisWeekJoinPending,
  })

  return (
    <>
      {showLoader && (
        <div role="status" aria-live="polite">
          <SrOnly>Finding coaches</SrOnly>
        </div>
      )}
      <Grid container spacing={2} justifyContent="center" className={classes.resultsGrid}>
        {showLoader && Array.from({ length: DIRECTORY_SKELETON_CARD_COUNT }, (_, index) => (
          <Grid
            key={`directory-skeleton-${index}`}
            item
            className={classes.resultsGridItem}
            xl={4}
            lg={4}
            md={4}
            sm={6}
            xs={12}
          >
            <DirectoryCoachCardSkeleton />
          </Grid>
        ))}
        {!showLoader && visibleHits.length > 0 && visibleHits.map((hit) => {
          return (
            <Grid
              data-gtm-coach-search-impr={hit.customSlug}
              data-gtm-coach-video={hit.hasIntroVideo}
              data-gtm-coach-bookable={hit.bookable && !!hit.bookingURL}
              data-gtm-card-banner={!!hit.hasIntroVideo}
              data-gtm-has-plus-subscription={hit.hasPlusSubscription}
              data-gtm-member-type={hit.memberType}
              data-gtm-certification-level={hit.certificationLevel}
              data-gtm-coach-accepts-leads={hit.acceptsLeads}
              data-gtm-coach-actionable={hit.acceptsLeads || (hit.bookable && !!hit.bookingURL)}
              data-gtm-book-this-week={isBookableThisWeek(hit.customSlug)}
              data-gtm-list-position={hit.__position}
              data-gtm-searched-location={placeSearchValue}
              key={keyForHit(hit)}
              item
              className={classes.resultsGridItem}
              xl={4}
              lg={4}
              md={4}
              sm={6}
              xs={12}
            >
              <CoachCardOptimized
                coachInfo={{ ...hit }}
                source="coach directory"
                isSaved={typeof isCoachSaved === 'function' ? isCoachSaved(hit.objectID) : false}
                onToggleSaved={onToggleSavedCoach}
                bookThisWeek={isBookableThisWeek(hit.customSlug)}
              />
            </Grid>
          )
        })}
        {!showLoader && visibleHits.length === 0 && (
          <NoResultsPage
            savedOnlyEmpty={savedOnlyEmpty}
            resultsContext={resultsString}
            onClearSavedOnly={savedOnly ? onToggleSavedOnly : undefined}
            onClearDirectoryFilters={onClearDirectoryFilters}
          />
        )}
      </Grid>
      {!savedOnly && (
        <LoadMoreButton hits={hits} hasMore={hasMore} refineNext={refineNext} />
      )}
    </>
  )
}

DirectoryHitsView.propTypes = {
  hits: PropTypes.array,
  hasMore: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  refineNext: PropTypes.func,
  refinePrevious: PropTypes.func,
  resultsString: PropTypes.string,
  placeSearchValue: PropTypes.string,
  savedOnly: PropTypes.bool,
  isCoachSaved: PropTypes.func,
  onToggleSavedCoach: PropTypes.func,
  onToggleSavedOnly: PropTypes.func,
  onClearDirectoryFilters: PropTypes.func,
  searching: PropTypes.bool,
  isSearchStalled: PropTypes.bool,
  searchResults: PropTypes.object,
  error: PropTypes.object,
  isLocationHydrating: PropTypes.bool,
  bookThisWeek: PropTypes.bool,
  bookThisWeekJoinReady: PropTypes.bool,
}

const DirectoryHitsViewWithSearchState = connectStateResults(DirectoryHitsView)

// Nested state-results (not extra InstantSearch props on the infinite-hits
// widget) so searching/searchResults changes do not retrigger Algolia.
export function CoachSearchInfiniteResultsToConnect(props) {
  return <DirectoryHitsViewWithSearchState {...props} />
}

const CoachSearchInfiniteResults = connectInfiniteHits(CoachSearchInfiniteResultsToConnect)

export default CoachSearchInfiniteResults
